import {
    Box,
    Grid,
    Switch,
    FormControlLabel,
    TextField,
    Typography,
    MenuItem,
    Paper,
    Stack,
    Divider,
    Button,
    Avatar,
    CircularProgress,
    Snackbar,
    Alert,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiClient from "../utils/apiClient";
import { toast } from "react-toastify";
import { getImageFullUrl } from "../utils/common";

export default function AdminProductManager() {
    const { productId } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);

    const [product, setProduct] = useState({
        isFeatured: false,
        showOnHomepage: false,
        isMostSelling: false,
        isBanner: false,
        isTrending: false,
        isFlashSale: false,
        isTodayDeal: false,
        isRecommended: false,
        isNewArrival: false,
         
        bannerTitle: "",
        bannerImage: "",
        bannerFile: null,
        approvalStatus: "pending",
        adminComment: "",
    });

    const [snack, setSnack] = useState({
        open: false,
        msg: "",
        severity: "success",
    });

    const handleChange = (field, value) => {
        setProduct((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    // 🔥 Fetch Product
    const fetchProduct = async () => {
        try {
            setLoading(true);
            const res = await apiClient.get(`/api/admin/product/${productId}`);
            const data = res?.data?.data;

            setProduct({
                isFeatured: data?.isFeatured || false,
                isTrending: data?.isTrending || false,
                isFlashSale: data?.isFlashSale || false,
                isTodayDeal: data?.isTodayDeal || false,
                isRecommended: data?.isRecommended || false,
                isNewArrival: data?.isNewArrival || false,
                showOnHomepage: data?.showOnHomepage || false,
                isMostSelling: data?.isMostSelling || false,
                isBanner: data?.isBanner || false,
                bannerTitle: data?.bannerTitle || "",
                bannerImage: data?.bannerImage || "",
                bannerFile: null,
                approvalStatus: data?.approvalStatus || "pending",
                adminComment: data?.adminComment || "",
            });

        } catch (err) {
            toast.error("Failed to load product", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (productId) fetchProduct();
    }, [productId]);

    // 🔥 Banner Upload
    const handleBannerUpload = (file) => {
        if (!file) return;
        if (!file) return;
        if (!["image/jpeg", "image/jpg", "image/png", "image/webp"].includes(file.type)) {
            toast.error("Only JPG, PNG, or WEBP allowed");
            return;
        }
        const preview = URL.createObjectURL(file);

        setProduct((prev) => ({
            ...prev,
            bannerImage: preview,
            bannerFile: file,
        }));
    };

    // 🔥 Submit Update
    const handleUpdate = async () => {
        try {
            const formData = new FormData();

            formData.append("isFeatured", product.isFeatured);
            formData.append("isTrending", product.isTrending);
            formData.append("isFlashSale", product.isFlashSale);
            formData.append("isTodayDeal", product.isTodayDeal);
            formData.append("isRecommended", product.isRecommended);
            formData.append("isNewArrival", product.isNewArrival);
            formData.append("showOnHomepage", product.showOnHomepage);
            formData.append("isMostSelling", product.isMostSelling);
            formData.append("isBanner", product.isBanner);
            formData.append("bannerTitle", product.bannerTitle);
            formData.append("approvalStatus", product.approvalStatus);
            formData.append("adminComment", product.adminComment);

            if (product.bannerFile) {
                formData.append("bannerImage", product.bannerFile);
            }

            await apiClient.put(
                `/api/admin/product/${productId}`,
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );

            toast.success("Updated successfully 🚀");

            setTimeout(() => {
                navigate("/product-list");
            }, 1000);

        } catch (err) {
            toast.error("Update failed", "error");
        }
    };

    if (loading) {
        return (
            <Box textAlign="center" mt={5}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography fontSize={18} fontWeight={700} mb={2}>
                Admin Product Controls
            </Typography>

            <Grid container spacing={2}>

                {/* TOGGLES */}
                <Grid item size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={product.isFeatured}
                                onChange={(e) =>
                                    handleChange("isFeatured", e.target.checked)
                                }
                            />
                        }
                        label="Featured"
                    />
                </Grid>
           
                <Grid item size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={product.isTrending}
                                onChange={(e) =>
                                    handleChange("isTrending", e.target.checked)
                                }
                            />
                        }
                        label="Trending"
                    />
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={product.isFlashSale}
                                onChange={(e) =>
                                    handleChange("isFlashSale", e.target.checked)
                                }
                            />
                        }
                        label="Flash Sale"
                    />
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={product.isTodayDeal}
                                onChange={(e) =>
                                    handleChange("isTodayDeal", e.target.checked)
                                }
                            />
                        }
                        label="Today Deal"
                    />
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={product.isRecommended}
                                onChange={(e) =>
                                    handleChange("isRecommended", e.target.checked)
                                }
                            />
                        }
                        label="Recommended"
                    />
                </Grid>
                <Grid item size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={product.isNewArrival}
                                onChange={(e) =>
                                    handleChange("isNewArrival", e.target.checked)
                                }
                            />
                        }
                        label="New Arrival"
                    />
                </Grid>

                <Grid item size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={product.showOnHomepage}
                                onChange={(e) =>
                                    handleChange("showOnHomepage", e.target.checked)
                                }
                            />
                        }
                        label="Homepage"
                    />
                </Grid>

                <Grid item size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={product.isMostSelling}
                                onChange={(e) =>
                                    handleChange("isMostSelling", e.target.checked)
                                }
                            />
                        }
                        label="Most Selling"
                    />
                </Grid>

                <Grid item size={{ xs: 12, md: 6 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={product.isBanner}
                                onChange={(e) =>
                                    handleChange("isBanner", e.target.checked)
                                }
                            />
                        }
                        label="Banner"
                    />
                </Grid>

                {/* BANNER */}
                {product.isBanner && (
                    <>
                        <Grid item size={{ xs: 12 }}>
                            <Divider />
                        </Grid>

                        <Grid item size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="Banner Title"
                                value={product.bannerTitle}
                                onChange={(e) =>
                                    handleChange("bannerTitle", e.target.value)
                                }
                            />
                        </Grid>

                        <Grid item size={{ xs: 12 }}>
                            <Button variant="outlined" component="label">
                                Upload Banner
                                <input
                                    hidden
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) =>
                                        handleBannerUpload(e.target.files[0])
                                    }
                                />
                            </Button>
                            <br />
                            {product.bannerImage && (
                                <img
                                    src={getImageFullUrl(product.bannerImage)}
                                    variant="rounded"
                                    style={{ maxWidth: "100%", maxHeight: 200, minHeight: 150, marginTop: 15 }}
                                />
                            )}
                        </Grid>
                    </>
                )}

                {/* APPROVAL */}
                <Grid item size={{ xs: 12 }}>
                    <Divider />
                </Grid>

                <Grid item size={{ xs: 12, md: 6 }} >
                    <TextField
                        select
                        fullWidth
                        label="Approval Status"
                        value={product.approvalStatus}
                        onChange={(e) =>
                            handleChange("approvalStatus", e.target.value)
                        }
                    >
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="approved">Approved</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                    </TextField>
                </Grid>

                <Grid item size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Admin Comment"
                        value={product.adminComment}
                        onChange={(e) =>
                            handleChange("adminComment", e.target.value)
                        }
                    />
                </Grid>

            </Grid>

            {/* ACTION */}
            <Stack direction="row" justifyContent="flex-end" mt={3}>
                <Button variant="contained" onClick={handleUpdate}>
                    Update Product
                </Button>
            </Stack>

            {/* TOAST */}
            <Snackbar
                open={snack.open}
                autoHideDuration={3000}
                onClose={() => setSnack({ ...snack, open: false })}
            >
                <Alert severity={snack.severity}>
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Paper>
    );
}